import {useState, useEffect} from 'react';
import {
  HarvestDemandPutSpeciesEnum,
  type HarvestDemandResponse,
  type HarvestDemandPost,
  type HarvestDemandPutCertificatesEnum,
} from 'client';

import {inputMenuActionType, methodType, SWRKeyEnum} from 'interfaces';

import backendCommunicator from 'services/backendCommunicator';
import useIsMounted from 'hooks/useIsMounted';
import {useInputMenuContext} from 'context/InputMenu';

import {type AxiosError} from 'axios';

import Alert from 'helpers/Alert';
import {useGetHarvestDemands} from 'hooks/swr';
import {mutate} from 'swr';

import useGetLongTermRange from 'hooks/useGetLongTermRange';
import {dateReader, kilosToTons, localDateSender, tonsToKilos} from 'helpers/UnitsFormatHandler';
import {startOfWeek} from 'date-fns';
import useParseURL from 'hooks/useParseURL';

function useAddEditFishType() {
  const [createUpdateHarvestDemand, setCreateUpdateHarvestDemand] = useState<boolean>(false);
  const {planId} = useParseURL();
  const {data: harvestDemands} = useGetHarvestDemands({resourceId: planId});
  const isMounted = useIsMounted();
  const {startDate} = useGetLongTermRange();
  const inputMenuContext = useInputMenuContext();
  const inputMenuState = inputMenuContext?.state;
  const inputMenuDispatch = inputMenuContext?.dispatch;

  const harvestDemandId = inputMenuState?.formData && inputMenuState?.formData.id;
  const method = inputMenuState?.method;

  const [harvestDemand, setHarvestDemand] = useState<IHarvestDemand>({
    endWeek: undefined,
    repeatEveryWeek: false,
    multiHarvests: false,
    startWeek: startDate ?? new Date(),
    name: '',
    volumePerWeek: 0,
    certificates: [],
    species: HarvestDemandPutSpeciesEnum.Salmon,
    planId,
    isActive: true,
  });

  const initialDemand = (harvestDemands: HarvestDemandResponse[]): IHarvestDemand => {
    const harvestDemand = harvestDemands.find((harvestDemand) => harvestDemand.id === harvestDemandId);
    const startWeek = harvestDemand ? dateReader(harvestDemand.startWeek) : new Date();
    const endWeek = harvestDemand?.endWeek ? dateReader(harvestDemand?.endWeek) : undefined;
    return {
      startWeek,
      endWeek,
      repeatEveryWeek: harvestDemand?.repeatEveryWeek ?? false,
      multiHarvests: harvestDemand?.multiHarvests ?? false,
      name: harvestDemand?.name ?? '',
      volumePerWeek: kilosToTons(harvestDemand?.volumePerWeek ?? 0),
      species: harvestDemand?.species ?? HarvestDemandPutSpeciesEnum.Salmon,
      certificates: harvestDemand?.certificates ?? [],
      isActive: harvestDemand?.isActive ?? true,
      planId: harvestDemand?.planId ?? '',
    };
  };

  useEffect(() => {
    harvestDemands && setHarvestDemand(initialDemand(harvestDemands));
  }, [harvestDemands]);

  const backendRequest = async () => {
    const harvestDemand2Send: HarvestDemandPost = {
      ...harvestDemand,
      startWeek: localDateSender(startOfWeek(harvestDemand.startWeek, {weekStartsOn: 1})),
      endWeek: harvestDemand.endWeek ? localDateSender(startOfWeek(harvestDemand.endWeek, {weekStartsOn: 1})) : null,
      volumePerWeek: tonsToKilos(harvestDemand.volumePerWeek),
      species: harvestDemand.species,
      planId,
      isActive: harvestDemand.isActive ?? true,
    };
    if (method === methodType.edit && harvestDemandId) {
      return await backendCommunicator.updateHarvestDemand(harvestDemandId, harvestDemand2Send);
    } else {
      return await backendCommunicator.addHarvestDemand(harvestDemand2Send);
    }
  };

  useEffect(() => {
    if (createUpdateHarvestDemand) {
      backendRequest()
        .then(() => {
          if (isMounted.current) {
            mutate([planId, SWRKeyEnum.GET_HARVEST_DEMANDS]);
            inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
          }
        })
        .catch((error: AxiosError) => {
          isMounted.current && Alert.error(error);
        })
        .finally(() => {
          isMounted.current && setCreateUpdateHarvestDemand(false);
        });
    }
  }, [createUpdateHarvestDemand]);

  return {harvestDemand, setHarvestDemand, setCreateUpdateHarvestDemand, createUpdateHarvestDemand};
}
export default useAddEditFishType;

export interface IHarvestDemand {
  endWeek?: Date | null;
  repeatEveryWeek: boolean;
  multiHarvests: boolean;
  startWeek: Date;
  name: string;
  volumePerWeek: number;
  species: HarvestDemandPutSpeciesEnum;
  certificates: HarvestDemandPutCertificatesEnum[];
  isActive: boolean;
  planId: string;
}
